// Copyright © 2026 Fannie Mae. All rights reserved.

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { resolveApiEndpointPath } from '../../core/config/api-endpoints.config';
import {
    BankCalculationRequest,
    BankCalculationResult,
    NonBankCalculationRequest,
    NonBankCalculationResult,
    ResolveSllWorksheetRequest,
    SllWorksheet,
    SllWorksheetPreview
} from './sll-calculation.models';

/** Wire shape returned by `POST /api/sll/worksheets/resolve` and `GET /api/sll/worksheets/{id}`. */
interface SllWorksheetApiDto {
  worksheetId: string;
  lenderId: number;
  lenderName: string | null;
  frameworkType: 'BANK' | 'NON_BANK';
  lenderTypeName: string | null;
  sllFramework: string | null;
  financialPeriod: string;
  availableFinancialPeriods: string[];
  preparerUserId: string | null;
  reviewerUserId: string | null;
  fnmPreparerName: string | null;
  fnmReviewerName: string | null;
  reviewStatus: string;
  reviewStatusName: string | null;
  reviewStatusDescription: string | null;
  agencyCreditRating: string | null;
  currentAssignedSll: string;
  lenderRequestedSllIndicator: boolean | null;
  lenderRequestedSllAmount: number | null;
  lastUpdatedAt: string;
  recalculationRequiredInd?: boolean;
}

function toWorksheet(dto: SllWorksheetApiDto): SllWorksheet {
  return {
    worksheetId: dto.worksheetId,
    lenderId: dto.lenderId,
    lenderName: dto.lenderName,
    frameworkType: dto.frameworkType,
    lenderTypeName: dto.lenderTypeName,
    sllFramework: dto.sllFramework,
    financialPeriod: dto.financialPeriod,
    availableFinancialPeriods: sortFinancialPeriodsDescending(dto.availableFinancialPeriods ?? []),
    preparerUserId: dto.preparerUserId,
    reviewerUserId: dto.reviewerUserId,
    fnmPreparerName: dto.fnmPreparerName,
    fnmReviewerName: dto.fnmReviewerName,
    reviewStatus: dto.reviewStatus,
    reviewStatusName: dto.reviewStatusName,
    reviewStatusDescription: dto.reviewStatusDescription,
    agencyCreditRating: dto.agencyCreditRating,
    currentAssignedSll: dto.currentAssignedSll,
    lenderRequestedSllIndicator: dto.lenderRequestedSllIndicator,
    lenderRequestedSllAmount: dto.lenderRequestedSllAmount,
    lastUpdatedAt: dto.lastUpdatedAt,
    recalculationRequiredInd: dto.recalculationRequiredInd
  };
}

function sortFinancialPeriodsDescending(periods: string[]): string[] {
  return [...periods].sort((left, right) => financialPeriodSortKey(right) - financialPeriodSortKey(left));
}

function financialPeriodSortKey(period: string): number {
  const match = /^Q([1-4])(\d{4})$/.exec(period);
  if (!match) {
    return Number.NEGATIVE_INFINITY;
  }
  return Number.parseInt(match[2], 10) * 10 + Number.parseInt(match[1], 10);
}

/**
 * SLL Calculation worksheet service — find-or-create + read, backed by the real
 * `ic1-mfrm-api` endpoints (`/api/sll/worksheets/resolve`, `/api/sll/worksheets/{id}`,
 * design.md §10.1). Endpoint paths are always resolved via `api-endpoints.config.ts` —
 * never hardcoded (project-context.md AI Implementation Constraint #4).
 */
@Injectable({ providedIn: 'root' })
export class SllCalculationService {
  private readonly http = inject(HttpClient);

  resolveOrCreate(request: ResolveSllWorksheetRequest): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('resolve-sll-worksheet');
    return this.http.post<SllWorksheetApiDto>(url, request).pipe(map(toWorksheet));
  }

  getById(worksheetId: string): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('get-sll-worksheet', { id: worksheetId });
    return this.http.get<SllWorksheetApiDto>(url).pipe(map(toWorksheet));
  }

  calculateBank(worksheetId: string, request: BankCalculationRequest): Observable<BankCalculationResult> {
    const url = resolveApiEndpointPath('calculate-sll-bank-worksheet', { id: worksheetId });
    return this.http.post<BankCalculationResult>(url, request);
  }

  getBankCalculation(worksheetId: string): Observable<BankCalculationResult> {
    const url = resolveApiEndpointPath('get-sll-bank-worksheet', { id: worksheetId });
    return this.http.get<BankCalculationResult>(url);
  }

  calculateNonBank(worksheetId: string, request: NonBankCalculationRequest): Observable<NonBankCalculationResult> {
    const url = resolveApiEndpointPath('calculate-sll-non-bank-worksheet', { id: worksheetId });
    return this.http.post<NonBankCalculationResult>(url, request);
  }

  getNonBankCalculation(worksheetId: string): Observable<NonBankCalculationResult> {
    const url = resolveApiEndpointPath('get-sll-non-bank-worksheet', { id: worksheetId });
    return this.http.get<NonBankCalculationResult>(url);
  }

  /** Step 5 (SLL-calc-ui-behavior.md 2026-10-05 revision, UI-GEN-006) — explicit Calculate action.
   *  Submits the caller's complete current draft calculation-driving inputs and returns a
   *  non-persistent preview; worksheet inputs no longer autosave, so exactly one of
   *  `bankInputs`/`nonBankInputs` must be supplied, matching the worksheet's `frameworkType`. */
  preview(
      worksheetId: string,
      request: { bankInputs?: BankCalculationRequest | null; nonBankInputs?: NonBankCalculationRequest | null }
  ): Observable<SllWorksheetPreview> {
    const url = resolveApiEndpointPath('preview-sll-worksheet', { id: worksheetId });
    return this.http.post<SllWorksheetPreview>(url, {
      bankInputs: request.bankInputs ?? null,
      nonBankInputs: request.nonBankInputs ?? null
    });
  }

  /** Step 5 (UI-GEN-007) — authoritative Save. Carries the caller's complete current draft
   *  calculation-driving inputs (`bankInputs`/`nonBankInputs`) — Save never depends on a prior
   *  Calculate/autosave; the backend independently reruns the applicable calculation.
   *  Preparer/Reviewer are never submitted — the API derives them from the authenticated user.
   *  `finalSll` (Story 1.7 FR48) — the browser's current draft Final SLL value; compared
   *  server-side with system-calculated value to determine override state.
   *  `lenderRequestedSllIndicator`/`lenderRequestedSllAmount` are interim metadata persisted
   *  on every Save (SLL-calc-ui-behavior.md §Lender Requested SLL). */
  save(
      worksheetId: string,
      finalSll?: number | null,
      overrideComment?: string | null,
      bankInputs?: BankCalculationRequest | null,
      nonBankInputs?: NonBankCalculationRequest | null,
      lenderRequestedSllIndicator?: boolean | null,
      lenderRequestedSllAmount?: number | null
  ): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('save-sll-worksheet', { id: worksheetId });
    return this.http
      .post<SllWorksheetApiDto>(url, {
        finalSll: finalSll ?? null,
        overrideComment: overrideComment ?? null,
        bankInputs: bankInputs ?? null,
        nonBankInputs: nonBankInputs ?? null,
        lenderRequestedSllIndicator: lenderRequestedSllIndicator ?? null,
        lenderRequestedSllAmount: lenderRequestedSllAmount ?? null
      })
      .pipe(map(toWorksheet));
  }

  submitToReviewer(worksheetId: string): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('submit-sll-to-reviewer', { id: worksheetId });
    return this.http.post<SllWorksheetApiDto>(url, {}).pipe(map(toWorksheet));
  }

  returnToPreparer(worksheetId: string): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('return-sll-to-preparer', { id: worksheetId });
    return this.http.post<SllWorksheetApiDto>(url, {}).pipe(map(toWorksheet));
  }

  approve(worksheetId: string): Observable<SllWorksheet> {
    const url = resolveApiEndpointPath('approve-sll-worksheet', { id: worksheetId });
    return this.http.post<SllWorksheetApiDto>(url, {}).pipe(map(toWorksheet));
  }

  /**
   * Story 1.9 — contextual Recalculate; visible/callable only when the worksheet is flagged
   * Recalculation Required. Returns a non-persisting preview using refreshed source/dependency
   * values (FR24) — the caller commits the refresh via a subsequent {@link save} call.
   */
  recalculate(worksheetId: string): Observable<SllWorksheetPreview> {
    const url = resolveApiEndpointPath('recalculate-sll-worksheet', { id: worksheetId });
    return this.http.post<SllWorksheetPreview>(url, {});
  }
}
